import { Album, AlbumFormat, Track, SyncMetadata } from '../types/album';

/**
 * Discogs API Integration Service
 * 
 * Handles fetching album metadata and cover images from Discogs API
 * Rate limited and respectful of API guidelines
 */

export interface DiscogsConfig {
  username: string;
  token: string;
  baseUrl: string;
}

export interface DiscogsRelease {
  id: number;
  title: string;
  artists: Array<{ name: string }>;
  year: number;
  genres: string[];
  styles: string[];
  formats?: Array<{
    name: string;
    qty: string;
    descriptions?: string[];
  }>;
  images?: Array<{
    type: string;
    uri: string;
    uri150: string;
    width: number;
    height: number;
  }>;
  master_id?: number;
  master_url?: string;
  tracklist?: Array<{
    position: string;
    title: string;
    duration?: string;
    artists?: Array<{ name: string }>;
  }>;
  resource_url?: string; // For tracking resource version/etag
  data_quality?: string; // Discogs data quality indicator
}

export interface DiscogsSearchResult {
  id: number;
  title: string;
  year: string;
  format: string[];
  label: string[];
  type: string;
  thumb: string;
  cover_image: string;
  resource_url: string;
}

export interface DiscogsCollectionItem {
  id: number;
  instance_id: number;
  basic_information: DiscogsSearchResult;
  date_added: string;
  rating: number;
}

export interface DiscogsCollectionResponse {
  pagination: {
    items: number;
    page: number;
    pages: number;
    per_page: number;
    urls: {
      next?: string;
      prev?: string;
      first: string;
      last: string;
    };
  };
  releases: DiscogsCollectionItem[];
}

export interface CollectionSyncProgress {
  stage: 'fetching' | 'processing' | 'complete' | 'error';
  currentPage: number;
  totalPages: number;
  currentItem: number;
  totalItems: number;
  currentAlbum: string;
  errors: string[];
  isIncremental?: boolean;
  newAlbums?: number;
  skippedAlbums?: number;
}

class DiscogsService {
  private config: DiscogsConfig | null = null;
  private requestDelay: number = 1000; // 1 second delay between requests
  private lastRequest: number = 0;
  private retryCount: number = 0;
  private maxRetries: number = 5;

  setConfig(config: DiscogsConfig): void {
    this.config = {
      ...config,
      baseUrl: config.baseUrl || 'https://api.discogs.com'
    };
  }

  getConfig(): DiscogsConfig | null {
    return this.config;
  }

  isConfigured(): boolean {
    return this.config !== null && 
           this.config.username !== '' && 
           this.config.token !== '';
  }

  /**
   * Search for releases by artist and album title
   */
  async searchReleases(artist: string, title: string): Promise<DiscogsSearchResult[]> {
    if (!this.isConfigured()) {
      throw new Error('Discogs service not configured');
    }

    await this.respectRateLimit();
    
    const query = encodeURIComponent(`${artist} ${title}`);
    const url = `${this.config!.baseUrl}/database/search?q=${query}&type=release&per_page=25`;
    
    try {
      const response = await this.makeRequest(url);
      return response.results || [];
    } catch (error) {
      console.error('Discogs search failed:', error);
      throw error;
    }
  }

  /**
   * Get detailed release information by ID
   */
  async getRelease(releaseId: number): Promise<DiscogsRelease> {
    if (!this.isConfigured()) {
      throw new Error('Discogs service not configured');
    }

    await this.respectRateLimit();
    
    const url = `${this.config!.baseUrl}/releases/${releaseId}`;
    
    try {
      return await this.makeRequest(url);
    } catch (error) {
      console.error(`Failed to get release ${releaseId}:`, error);
      throw error;
    }
  }

  /**
   * Get master release information for original year and better metadata
   */
  async getMasterRelease(masterId: number): Promise<any> {
    if (!this.isConfigured()) {
      throw new Error('Discogs service not configured');
    }

    await this.respectRateLimit();
    
    const url = `${this.config!.baseUrl}/masters/${masterId}`;
    
    try {
      return await this.makeRequest(url);
    } catch (error) {
      console.error(`Failed to get master release ${masterId}:`, error);
      throw error;
    }
  }

  /**
   * Get enhanced release data by fetching both release and master release
   */
  async getEnhancedRelease(releaseId: number): Promise<DiscogsRelease & { originalYear?: number; masterGenres?: string[]; masterStyles?: string[] }> {
    const release = await this.getRelease(releaseId);
    
    // If this release has a master, fetch additional data from it
    if (release.master_id) {
      try {
        const master = await this.getMasterRelease(release.master_id);
        
        return {
          ...release,
          originalYear: (typeof master.year === 'string' ? parseInt(master.year, 10) || 0 : master.year) || 
                        (typeof release.year === 'string' ? parseInt(release.year, 10) || 0 : release.year), // Use master year as original year
          masterGenres: master.genres || [],
          masterStyles: master.styles || []
        };
      } catch (error) {
        console.warn(`Could not fetch master release ${release.master_id}:`, error);
        // Return original release data if master fetch fails
        return release;
      }
    }
    
    return release;
  }

  /**
   * Download cover image and return as blob using HTML Image element to bypass CORS
   */
  async downloadCoverImage(imageUrl: string): Promise<Blob> {
    if (!imageUrl) {
      throw new Error('No image URL provided');
    }

    await this.respectRateLimit();

    try {
      console.log('Downloading image from:', imageUrl);
      
      // Try direct fetch first (works for some images)
      try {
        const response = await fetch(imageUrl, {
          method: 'GET',
          mode: 'cors',
        });
        
        if (response.ok) {
          const blob = await response.blob();
          if (blob.size > 0) {
            console.log(`Direct fetch successful, size: ${blob.size} bytes`);
            return blob;
          }
        }
      } catch (fetchError) {
        console.log('Direct fetch failed, trying alternative method:', fetchError);
      }
      
      // Fallback: Use HTML Image element to load the image, then convert to blob
      return await this.downloadImageViaCanvas(imageUrl);
      
    } catch (error) {
      console.error('Image download failed for URL:', imageUrl, error);
      throw new Error(`Image download failed: ${error instanceof Error ? error.message : error}`);
    }
  }
  
  /**
   * Download image using HTML Image element and Canvas to convert to blob
   */
  private async downloadImageViaCanvas(imageUrl: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Try to enable CORS
      
      const timeout = setTimeout(() => {
        reject(new Error('Image load timeout'));
      }, 30000);
      
      img.onload = () => {
        clearTimeout(timeout);
        try {
          // Create canvas and draw image
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx.drawImage(img, 0, 0);
          
          // Convert canvas to blob
          canvas.toBlob((blob) => {
            if (blob && blob.size > 0) {
              console.log(`Canvas conversion successful, size: ${blob.size} bytes`);
              resolve(blob);
            } else {
              reject(new Error('Failed to convert canvas to blob'));
            }
          }, 'image/jpeg', 0.9);
          
        } catch (canvasError) {
          reject(new Error(`Canvas processing failed: ${canvasError}`));
        }
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Failed to load image'));
      };
      
      // Start loading the image
      img.src = imageUrl;
    });
  }

  /**
   * Clean artist name by removing Discogs disambiguation numbers
   */
  private cleanArtistName(artistName: string): string {
    // Remove disambiguation numbers like (2), (3), etc. from artist names
    return artistName.replace(/\s*\(\d+\)\s*$/, '').trim();
  }

  /**
   * Analyze Discogs format data to determine album type, packaging, and physical size
   */
  public analyzeFormat(formats?: Array<{ name: string; qty: string; descriptions?: string[] }>): AlbumFormat {
    if (!formats || formats.length === 0) {
      return {
        type: 'Unknown',
        packaging: 'Unknown',
        discCount: 1,
        physicalSize: 1.0,
        formatDetails: []
      };
    }

    // Collect all format information
    const allDescriptions = formats.flatMap(f => [f.name, ...(f.descriptions || [])]);
    const formatDetails = allDescriptions.filter(Boolean);
    
    // Determine album type
    let type: AlbumFormat['type'] = 'Unknown';
    const allText = allDescriptions.join(' ');

    if (allDescriptions.some(d => d.includes('LP') || d.includes('Album'))) {
      type = 'LP';
    } else if (allDescriptions.some(d => d.includes('EP'))) {
      type = 'EP';
    } else if (allDescriptions.some(d => d.includes('Single'))) {
      type = 'Single';
    } else if (allDescriptions.some(d => d.includes('Box'))) {
      type = 'Box Set';
    } else if (allDescriptions.some(d => d.includes('Compilation'))) {
      type = 'Compilation';
    } else if (allDescriptions.some(d => d === 'Vinyl')) {
      // Vinyl without explicit type - determine by size
      if (allText.includes('12"') || allText.includes('12 "')) {
        type = 'LP';
      } else if (allText.includes('10"') || allText.includes('10 "')) {
        type = 'EP';
      } else if (allText.includes('7"') || allText.includes('7 "')) {
        type = 'Single';
      } else {
        // Default vinyl to LP if no size specified
        type = 'LP';
      }
    }

    // Determine packaging
    let packaging: AlbumFormat['packaging'] = 'Standard';
    if (allDescriptions.some(d => d.toLowerCase().includes('gatefold'))) {
      packaging = 'Gatefold';
    } else if (allDescriptions.some(d => d.toLowerCase().includes('tri-fold'))) {
      packaging = 'Tri-fold';
    } else if (allDescriptions.some(d => d.toLowerCase().includes('box'))) {
      packaging = 'Box';
    } else if (allDescriptions.some(d => d.toLowerCase().includes('digipak'))) {
      packaging = 'Digipak';
    }

    // Calculate disc count with enhanced detection
    let discCount = 1;
    
    // Calculate disc count by analyzing each format line individually
    // This handles cases like "qty: 2" with "2×LP" = 4 total LPs
    const vinylFormats = formats.filter(f =>
      f.name && (
        f.name.toLowerCase().includes('vinyl') ||
        f.name.toLowerCase().includes('lp') ||
        f.name.toLowerCase().includes('record')
      )
    );

    const formatsToAnalyze = vinylFormats.length > 0 ? vinylFormats : formats;

    // Sum quantities across all vinyl format lines
    discCount = 0;
    for (const format of formatsToAnalyze) {
      const qty = parseInt(format.qty) || 1;
      const formatText = [format.name, ...(format.descriptions || [])].join(' ').toLowerCase();

      // Look for explicit multipliers like "2×LP", "3xLP", "Double LP"
      // Be careful not to match record sizes (7", 10", 12")
      let multiplier = 1;

      // Pattern 1: "2×LP", "3xLP", "4 x Vinyl" - explicit multiplier with × or x
      const crossMatch = formatText.match(/(\d+)\s*(?:×|x)\s*(?:lp|disc|record|vinyl)/i);
      if (crossMatch) {
        const num = parseInt(crossMatch[1]);
        if (num && num > 1 && num < 20) { // Reasonable range for disc count
          multiplier = num;
        }
      }

      // Pattern 2: "Double LP", "Triple Album", etc.
      if (multiplier === 1) {
        if (/\bdouble\s*(?:lp|album|disc|vinyl)/i.test(formatText)) {
          multiplier = 2;
        } else if (/\btriple\s*(?:lp|album|disc|vinyl)/i.test(formatText)) {
          multiplier = 3;
        } else if (/\bquadruple\s*(?:lp|album|disc|vinyl)/i.test(formatText)) {
          multiplier = 4;
        }
      }

      // Total for this format line = qty × multiplier
      const lineTotal = qty * multiplier;
      discCount += lineTotal;

      console.log('Format analysis:', {
        name: format.name,
        qty,
        formatText,
        multiplier,
        lineTotal,
        runningTotal: discCount
      });
    }

    // Ensure at least 1 disc
    if (discCount === 0) {
      discCount = 1;
    }
    
    // Additional check for box set indicators
    if (type === 'Box Set' && discCount === 1) {
      // Box sets are typically multi-disc, assume at least 2 if not detected
      discCount = 2;
    }

    // Calculate physical size multiplier with smart format analysis
    let physicalSize = 1.0; // Standard LP thickness
    
    // Analyze individual formats to calculate cumulative thickness
    let totalThickness = 0;
    
    for (const format of formats) {
      const qty = parseInt(format.qty) || 1;
      const name = format.name?.toLowerCase() || '';
      const descriptions = (format.descriptions || []).join(' ').toLowerCase();
      const formatText = `${name} ${descriptions}`;
      
      // Determine thickness per unit for this format
      let unitThickness = 1.0; // Default LP thickness
      
      if (formatText.includes('7"') || formatText.includes('7 inch') || formatText.includes('single')) {
        unitThickness = 0.3; // 7" singles are much thinner
      } else if (formatText.includes('cd') && !formatText.includes('vinyl')) {
        unitThickness = 0.2; // Bonus CDs are very thin
      } else if (formatText.includes('dvd') && !formatText.includes('vinyl')) {
        unitThickness = 0.2; // Bonus DVDs are very thin
      } else if (formatText.includes('ep') || formatText.includes('10"') || formatText.includes('10 inch')) {
        unitThickness = 1.0; // EPs and 10" records same as LP
      } else if (formatText.includes('12"') || formatText.includes('12 inch') || formatText.includes('lp') || formatText.includes('vinyl')) {
        unitThickness = 1.0; // Standard LP thickness
      } else if (type === 'Single') {
        unitThickness = 0.8; // Generic single
      }
      
      totalThickness += (qty * unitThickness);
    }
    
    // Use calculated thickness, with minimum of 1.0 for standard releases
    physicalSize = Math.max(totalThickness, type === 'Single' ? 0.8 : 1.0);
    
    // Apply packaging multipliers (these are additive to disc count)
    if (type === 'Box Set') {
      // Box sets get additional thickness for packaging
      physicalSize = physicalSize * 1.3;
    } else if (packaging === 'Gatefold') {
      // Gatefolds add thickness for the fold
      physicalSize = Math.max(physicalSize, 1.5);
    } else if (packaging === 'Tri-fold') {
      // Tri-folds add more thickness
      physicalSize = Math.max(physicalSize, 2.0);
    } else if (packaging === 'Box') {
      // Box packaging adds some thickness
      physicalSize = physicalSize * 1.2;
    }

    return {
      type,
      packaging,
      discCount,
      physicalSize,
      formatDetails
    };
  }

  /**
   * Re-analyze format data from stored formatDetails
   */
  public analyzeFormatFromDetails(formatDetails: string[]): AlbumFormat {
    // Convert formatDetails back to format structure for analysis
    const formats = formatDetails.map(detail => ({
      name: detail.includes('Vinyl') ? 'Vinyl' : detail,
      qty: '1', // Default, will be overridden by analysis
      descriptions: [detail]
    }));
    
    return this.analyzeFormat(formats);
  }

  /**
   * Enhance genres by prioritizing more specific styles over broad genres
   */
  private enhanceGenres(genres: string[], styles: string[]): string[] {
    // Combine and deduplicate
    const allGenres = [...genres, ...styles];
    const uniqueGenres = [...new Set(allGenres)];
    
    // Remove overly broad genres if more specific ones exist
    const broadGenres = ['Electronic', 'Rock', 'Pop', 'Jazz', 'Folk, World, & Country', 'Classical'];
    const specificGenres = uniqueGenres.filter(g => !broadGenres.includes(g));
    
    // If we have specific genres, filter out broad ones that are redundant
    if (specificGenres.length > 0) {
      return uniqueGenres.filter(genre => {
        // Keep specific genres and non-redundant broad genres
        if (!broadGenres.includes(genre)) return true;
        
        // For broad genres, only keep if no specific sub-genre exists
        if (genre === 'Electronic' && specificGenres.some(g => 
          ['Techno', 'House', 'Ambient', 'IDM', 'Drum n Bass', 'Breakbeat', 'Trance', 'Dubstep'].includes(g)
        )) return false;
        
        if (genre === 'Rock' && specificGenres.some(g => 
          ['Indie Rock', 'Alternative Rock', 'Prog Rock', 'Punk', 'Metal', 'Hard Rock', 'Psychedelic Rock'].includes(g)
        )) return false;
        
        return true;
      });
    }
    
    return uniqueGenres.slice(0, 8); // Limit to 8 genres max
  }

  /**
   * Extract tracklist from Discogs release data
   */
  private extractTracklist(release: DiscogsRelease): Track[] {
    if (!release.tracklist || release.tracklist.length === 0) {
      return [];
    }

    return release.tracklist.map(track => ({
      position: track.position || '',
      title: track.title || 'Unknown Track',
      duration: track.duration,
      artists: track.artists ? track.artists.map(artist => this.cleanArtistName(artist.name)) : undefined
    }));
  }

  /**
   * Convert Discogs release to Album format with enhanced master release data
   */
  convertToAlbum(release: DiscogsRelease & { originalYear?: number; masterGenres?: string[]; masterStyles?: string[] }, customId?: string): Partial<Album> {
    // Extract all artists from Discogs data
    const allArtists = (release.artists || []).map(artist => this.cleanArtistName(artist.name));
    
    // Primary artist (first one for backward compatibility)
    const primaryArtist = allArtists[0] || 'Unknown Artist';
    
    // Clean up duplicates and filter out empty names
    const cleanedArtists = [...new Set(allArtists.filter(name => name && name.trim()))];
    
    // Combine genres and styles from both release and master release (if available)
    const releaseGenres = [...(release.genres || []), ...(release.masterGenres || [])];
    const releaseStyles = [...(release.styles || []), ...(release.masterStyles || [])];
    
    // Use enhanced genre processing to get better quality genre data
    const enhancedGenres = this.enhanceGenres(releaseGenres, releaseStyles);
    
    // Parse years properly - originalYear from master, releaseYear from release
    const parseYear = (year: any): number => {
      if (!year) return 0;
      return typeof year === 'string' ? parseInt(year, 10) || 0 : year;
    };

    const originalYear = parseYear(release.originalYear) || parseYear(release.year);
    const releaseYear = parseYear(release.year);

    console.log('Discogs convertToAlbum year debug:', {
      title: release.title,
      originalYear: release.originalYear,
      releaseYear: release.year,
      parsedOriginalYear: originalYear,
      parsedReleaseYear: releaseYear
    });
    
    // Analyze format information
    const formatInfo = this.analyzeFormat(release.formats);
    
    // Extract tracklist
    const tracklist = this.extractTracklist(release);
    
    // Create sync metadata
    const syncMetadata: SyncMetadata = {
      lastSyncTime: new Date().toISOString(),
      lastDiscogsSyncTime: new Date().toISOString(),
      discogsResourceVersion: release.resource_url,
      customFields: {}, // No custom fields yet
      syncSources: {
        discogs: true,
        manual: false
      }
    };
    
    return {
      id: customId || `discogs-${release.id}`,
      artist: primaryArtist,
      artists: cleanedArtists,
      title: release.title,
      year: originalYear,
      releaseYear: releaseYear,
      genres: enhancedGenres,
      isVariousArtists: cleanedArtists.some(artist => artist.toLowerCase().includes('various')),
      discogsId: release.id.toString(),
      masterId: release.master_id?.toString(),
      format: formatInfo,
      tracklist: tracklist.length > 0 ? tracklist : undefined,
      syncMetadata,
      // Note: coverImageBlob will be set separately after download
    };
  }

  /**
   * Get user's collection (requires authentication) - Basic method
   */
  async getUserCollection(username?: string): Promise<DiscogsSearchResult[]> {
    if (!this.isConfigured()) {
      throw new Error('Discogs service not configured');
    }

    const user = username || this.config!.username;
    await this.respectRateLimit();
    
    const url = `${this.config!.baseUrl}/users/${user}/collection/folders/0/releases?per_page=100`;
    
    try {
      const response = await this.makeRequest(url);
      return response.releases?.map((item: any) => item.basic_information) || [];
    } catch (error) {
      console.error('Failed to get user collection:', error);
      throw error;
    }
  }

  /**
   * Get complete user collection with pagination support and progress tracking
   */
  async syncCompleteCollection(
    onProgress?: (progress: CollectionSyncProgress) => void,
    username?: string
  ): Promise<DiscogsCollectionItem[]> {
    if (!this.isConfigured()) {
      throw new Error('Discogs service not configured');
    }

    const user = username || this.config!.username;
    const allReleases: DiscogsCollectionItem[] = [];
    let currentPage = 1;
    let totalPages = 1;
    let totalItems = 0;

    try {
      // First, get the first page to determine pagination info
      onProgress?.({
        stage: 'fetching',
        currentPage: 1,
        totalPages: 1,
        currentItem: 0,
        totalItems: 0,
        currentAlbum: 'Fetching collection info...',
        errors: []
      });

      await this.respectRateLimit();
      const firstPageUrl = `${this.config!.baseUrl}/users/${user}/collection/folders/0/releases?per_page=100&page=1`;
      const firstResponse: DiscogsCollectionResponse = await this.makeRequest(firstPageUrl);
      
      totalPages = firstResponse.pagination.pages;
      totalItems = firstResponse.pagination.items;
      allReleases.push(...firstResponse.releases);

      onProgress?.({
        stage: 'fetching',
        currentPage: 1,
        totalPages,
        currentItem: firstResponse.releases.length,
        totalItems,
        currentAlbum: `Page 1/${totalPages} - ${firstResponse.releases.length} albums`,
        errors: []
      });

      // Fetch remaining pages if any
      for (currentPage = 2; currentPage <= totalPages; currentPage++) {
        await this.respectRateLimit();
        
        const pageUrl = `${this.config!.baseUrl}/users/${user}/collection/folders/0/releases?per_page=100&page=${currentPage}`;
        const pageResponse: DiscogsCollectionResponse = await this.makeRequest(pageUrl);
        
        allReleases.push(...pageResponse.releases);

        onProgress?.({
          stage: 'fetching',
          currentPage,
          totalPages,
          currentItem: allReleases.length,
          totalItems,
          currentAlbum: `Page ${currentPage}/${totalPages} - ${pageResponse.releases.length} albums`,
          errors: []
        });
      }

      onProgress?.({
        stage: 'complete',
        currentPage: totalPages,
        totalPages,
        currentItem: allReleases.length,
        totalItems,
        currentAlbum: `Collection fetch complete: ${allReleases.length} albums`,
        errors: []
      });

      return allReleases;

    } catch (error: any) {
      console.error('Failed to sync collection:', error);
      
      onProgress?.({
        stage: 'error',
        currentPage,
        totalPages,
        currentItem: allReleases.length,
        totalItems,
        currentAlbum: 'Sync failed',
        errors: [error.message || 'Unknown error occurred']
      });

      throw error;
    }
  }

  /**
   * Get collection info without fetching all items
   */
  async getCollectionInfo(username?: string): Promise<{ totalItems: number; totalPages: number }> {
    if (!this.isConfigured()) {
      throw new Error('Discogs service not configured');
    }

    const user = username || this.config!.username;
    await this.respectRateLimit();
    
    const url = `${this.config!.baseUrl}/users/${user}/collection/folders/0/releases?per_page=1&page=1`;
    
    try {
      const response: DiscogsCollectionResponse = await this.makeRequest(url);
      return {
        totalItems: response.pagination.items,
        totalPages: response.pagination.pages
      };
    } catch (error) {
      console.error('Failed to get collection info:', error);
      throw error;
    }
  }

  /**
   * Sync only new additions to collection (incremental sync)
   * Compares against existing albums by Discogs ID and instance ID
   */
  async syncNewAlbums(
    existingAlbums: Album[],
    onProgress?: (progress: CollectionSyncProgress) => void,
    username?: string
  ): Promise<DiscogsCollectionItem[]> {
    if (!this.isConfigured()) {
      throw new Error('Discogs service not configured');
    }

    const user = username || this.config!.username;
    
    // Create sets for quick lookup of existing albums
    const existingDiscogsIds = new Set(
      existingAlbums
        .filter(album => album.discogsId)
        .map(album => album.discogsId)
    );
    
    const existingInstanceIds = new Set(
      existingAlbums
        .filter(album => album.id.startsWith('collection-'))
        .map(album => album.id.replace('collection-', ''))
    );

    const newReleases: DiscogsCollectionItem[] = [];
    let currentPage = 1;
    let totalPages = 1;
    let totalItems = 0;
    let skippedCount = 0;

    try {
      // Get first page to determine pagination
      onProgress?.({
        stage: 'fetching',
        currentPage: 1,
        totalPages: 1,
        currentItem: 0,
        totalItems: 0,
        currentAlbum: 'Checking for new albums...',
        errors: [],
        isIncremental: true,
        newAlbums: 0,
        skippedAlbums: 0
      });

      await this.respectRateLimit();
      const firstPageUrl = `${this.config!.baseUrl}/users/${user}/collection/folders/0/releases?per_page=100&page=1&sort=added&sort_order=desc`;
      const firstResponse: DiscogsCollectionResponse = await this.makeRequest(firstPageUrl);
      
      totalPages = firstResponse.pagination.pages;
      totalItems = firstResponse.pagination.items;

      // Process first page
      for (const item of firstResponse.releases) {
        const instanceId = item.instance_id.toString();
        const discogsId = item.basic_information.id.toString();
        
        if (existingInstanceIds.has(instanceId) || existingDiscogsIds.has(discogsId)) {
          skippedCount++;
        } else {
          newReleases.push(item);
        }
      }

      onProgress?.({
        stage: 'fetching',
        currentPage: 1,
        totalPages,
        currentItem: firstResponse.releases.length,
        totalItems,
        currentAlbum: `Page 1/${totalPages} - Found ${newReleases.length} new albums`,
        errors: [],
        isIncremental: true,
        newAlbums: newReleases.length,
        skippedAlbums: skippedCount
      });

      // Fetch remaining pages
      for (currentPage = 2; currentPage <= totalPages; currentPage++) {
        await this.respectRateLimit();
        
        const pageUrl = `${this.config!.baseUrl}/users/${user}/collection/folders/0/releases?per_page=100&page=${currentPage}&sort=added&sort_order=desc`;
        const pageResponse: DiscogsCollectionResponse = await this.makeRequest(pageUrl);
        
        let pageNewCount = 0;
        for (const item of pageResponse.releases) {
          const instanceId = item.instance_id.toString();
          const discogsId = item.basic_information.id.toString();
          
          if (existingInstanceIds.has(instanceId) || existingDiscogsIds.has(discogsId)) {
            skippedCount++;
          } else {
            newReleases.push(item);
            pageNewCount++;
          }
        }

        onProgress?.({
          stage: 'fetching',
          currentPage,
          totalPages,
          currentItem: currentPage * 100,
          totalItems,
          currentAlbum: `Page ${currentPage}/${totalPages} - Found ${pageNewCount} new albums`,
          errors: [],
          isIncremental: true,
          newAlbums: newReleases.length,
          skippedAlbums: skippedCount
        });

        // Early termination: if we're finding mostly existing albums in recent pages,
        // it's likely we've found all new ones (since sorted by date added desc)
        if (pageNewCount === 0 && currentPage > 2) {
          console.log('No new albums found in recent pages, stopping incremental sync');
          break;
        }
      }

      onProgress?.({
        stage: 'complete',
        currentPage: Math.min(currentPage, totalPages),
        totalPages,
        currentItem: newReleases.length + skippedCount,
        totalItems,
        currentAlbum: `Incremental sync complete: ${newReleases.length} new albums found`,
        errors: [],
        isIncremental: true,
        newAlbums: newReleases.length,
        skippedAlbums: skippedCount
      });

      return newReleases;

    } catch (error: any) {
      console.error('Failed to sync new albums:', error);
      
      onProgress?.({
        stage: 'error',
        currentPage,
        totalPages,
        currentItem: newReleases.length + skippedCount,
        totalItems,
        currentAlbum: 'Incremental sync failed',
        errors: [error.message || 'Unknown error occurred'],
        isIncremental: true,
        newAlbums: newReleases.length,
        skippedAlbums: skippedCount
      });

      throw error;
    }
  }

  /**
   * Test connection with current configuration
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    if (!this.isConfigured()) {
      return { success: false, message: 'Discogs service not configured' };
    }

    try {
      await this.respectRateLimit();
      const url = `${this.config!.baseUrl}/users/${this.config!.username}`;
      await this.makeRequest(url);
      return { success: true, message: 'Connection successful' };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.message || 'Connection failed' 
      };
    }
  }

  private async respectRateLimit(): Promise<void> {
    const elapsed = Date.now() - this.lastRequest;
    if (elapsed < this.requestDelay) {
      const waitTime = this.requestDelay - elapsed;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    this.lastRequest = Date.now();
  }

  private async makeRequest(url: string): Promise<any> {
    return this.makeRequestWithRetry(url, 0);
  }

  private async makeRequestWithRetry(url: string, attemptNumber: number): Promise<any> {
    // Respect rate limit before making request
    await this.respectRateLimit();
    
    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Discogs token=${encodeURIComponent(this.config!.token)}`,
          'User-Agent': `RecordCollectionBrowser/1.0 +https://github.com/user/record-browser`,
          'Accept': 'application/vnd.discogs.v2.json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid Discogs token');
        }
        
        if (response.status === 429) {
          // Rate limited - implement exponential backoff
          if (attemptNumber < this.maxRetries) {
            const backoffDelay = this.calculateBackoffDelay(attemptNumber);
            console.log(`Rate limited (429). Waiting ${backoffDelay}ms before retry ${attemptNumber + 1}/${this.maxRetries}`);
            
            await new Promise(resolve => setTimeout(resolve, backoffDelay));
            return this.makeRequestWithRetry(url, attemptNumber + 1);
          } else {
            throw new Error(`Rate limited by Discogs API after ${this.maxRetries} retries`);
          }
        }
        
        // For other 5xx errors, also retry with exponential backoff
        if (response.status >= 500 && attemptNumber < this.maxRetries) {
          const backoffDelay = this.calculateBackoffDelay(attemptNumber);
          console.log(`Server error (${response.status}). Waiting ${backoffDelay}ms before retry ${attemptNumber + 1}/${this.maxRetries}`);
          
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return this.makeRequestWithRetry(url, attemptNumber + 1);
        }
        
        throw new Error(`Discogs API error: ${response.status} ${response.statusText}`);
      }

      // Reset retry count on successful request
      this.retryCount = 0;
      return await response.json();
      
    } catch (error) {
      // For network errors, also implement retry with exponential backoff
      if (attemptNumber < this.maxRetries && this.isNetworkError(error)) {
        const backoffDelay = this.calculateBackoffDelay(attemptNumber);
        console.log(`Network error. Waiting ${backoffDelay}ms before retry ${attemptNumber + 1}/${this.maxRetries}:`, error);
        
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return this.makeRequestWithRetry(url, attemptNumber + 1);
      }
      
      throw error;
    }
  }

  private calculateBackoffDelay(attemptNumber: number): number {
    // Exponential backoff: 2^attempt * 1000ms + jitter
    // Attempt 0: 1000ms, Attempt 1: 2000ms, Attempt 2: 4000ms, etc.
    const baseDelay = Math.pow(2, attemptNumber) * 1000;
    const jitter = Math.random() * 1000; // Add up to 1 second of random jitter
    return Math.min(baseDelay + jitter, 30000); // Cap at 30 seconds
  }

  private isNetworkError(error: any): boolean {
    return error instanceof TypeError || 
           (error.message && (
             error.message.includes('fetch') ||
             error.message.includes('network') ||
             error.message.includes('Failed to fetch')
           ));
  }
}

// Singleton instance
export const discogsService = new DiscogsService();